import React, { ComponentType, ReactElement } from "react";
// 1. Import the new CSS file
import "./animatedIcon.css";

// --- Refactor of createAnimatedIcon ---

/**
 * Make a new animation icon component.
 * This is a Higher-Order Component (HOC) that wraps a given
 * component and applies the .icon-animated class to it.
 */
export function createAnimatedIcon<P extends { className?: string }>(
    WrappedComponent: ComponentType<P>
): ComponentType<P> {
    
    // Create a new component that renders the wrapped component
    const AnimatedWrapper: React.FC<P> = (props) => {
        const { className, ...rest } = props;

        // 2. Combine the animation class with any passed-in class
        const combinedClassName = [
            "icon-animated", // The animation class from animated_icon.css
            className,       // Any class passed from the parent
        ]
            .filter(Boolean)
            .join(" ");

        // 3. Render the original component with the new class
        return <WrappedComponent {...(rest as P)} className={combinedClassName} />;
    };

    // Set a display name for better debugging
    const wrappedName =
        WrappedComponent.displayName || WrappedComponent.name || "Component";
    AnimatedWrapper.displayName = `withAnimation(${wrappedName})`;

    return AnimatedWrapper;
}

// --- Refactor of the default export ---

interface AnimatedIconProps {
    /**
     * Icon children
     */
    children: ReactElement<any>;
    className?: string;
}

/**
 * Spin animation icon.
 * This component clones its single child and adds the animation class.
 */
const AnimatedIcon: React.FC<AnimatedIconProps> = ({ children, className }) => {
    const child = React.Children.only(children);

    // Combine all class names
    const combinedClassName = [
        "icon-animated",       // The animation class
        child.props.className, // Class from the child itself
        className,             // Class from AnimatedIcon's props
    ]
        .filter(Boolean)
        .join(" ");

    // Clone the child, passing in the new combined class name
    return React.cloneElement(child, {
        ...child.props,
        className: combinedClassName,
    });
};

export default AnimatedIcon;

